// import fillerPlants from '../data/fillerPlants.json';
import { supabase } from '../../components/lib/supabaseClient';

export const calculateGardenEfficiency = (gardenMatrix, plantMap) => {
    const totalSoilCells = gardenMatrix.flat().filter(cell => cell === 1).length;
    const occupiedCells = plantMap.flat().filter(cell => cell !== null).length;

    return totalSoilCells > 0 ? (occupiedCells / totalSoilCells) * 100 : 0;
};

// Helper function to map database properties to expected properties
const mapPlantProperties = (plant) => ({
    ...plant,
    id: plant.id,
    name: plant.plant_name || plant.name,
    image: plant.plant_image_url || plant.image,
    veggieImage: plant.plant_image_url || plant.veggieImage,
    plantingWidth: plant.planting_width || plant.plantingWidth || 1,
    plantingHeight: plant.planting_height || plant.plantingHeight || 1,
});

// Fix 1: Update validatePlacement to match the call order
export const validatePlacement = (plant, plantMap, gardenMatrix, x, y) => {
    const mappedPlant = mapPlantProperties(plant);
    const { plantingWidth = 1, plantingHeight = 1 } = mappedPlant;
    const rows = gardenMatrix.length;
    const cols = gardenMatrix[0]?.length || 0;

    if (y + plantingHeight > rows || x + plantingWidth > cols) {
        return false;
    }

    for (let r = 0; r < plantingHeight; r++) {
        for (let c = 0; c < plantingWidth; c++) {
            const targetRow = y + r;
            const targetCol = x + c;

            if (gardenMatrix[targetRow][targetCol] !== 1 || plantMap[targetRow][targetCol]) {
                return false;
            }
        }
    }

    return true;
};

export const createPlantMap = (input, colsMaybe) => {
    if (Array.isArray(input)) {
        // Old format: matrix passed in
        return input.map(row => row.map(() => null));
    } else {
        const rows = input;
        const cols = colsMaybe;
        return Array.from({ length: rows }, () =>
            Array.from({ length: cols }, () => null)
        );
    }
};

export const placePlant = (plantMap, plant, row, col, color) => {
    const mappedPlant = mapPlantProperties(plant);
    const { plantingWidth = 1, plantingHeight = 1 } = mappedPlant;

    // Deep copy the 2D array
    const newMap = plantMap.map(row => [...row]);

    for (let r = 0; r < plantingHeight; r++) {
        for (let c = 0; c < plantingWidth; c++) {
            const targetRow = row + r;
            const targetCol = col + c;

            // SAFETY CHECK: avoid writing out of bounds
            if (
                newMap[targetRow] &&
                typeof newMap[targetRow][targetCol] !== 'undefined'
            ) {
                newMap[targetRow][targetCol] = {
                    ...mappedPlant, // Use mapped properties
                    rootCell: r === 0 && c === 0,
                    color: color || mappedPlant.color || generatePlantColor(mappedPlant.name)
                };
            } else {
                console.warn(
                    `placePlant: skipping out-of-bounds cell (${targetRow}, ${targetCol})`
                );
            }
        }
    }

    return newMap;
};

// Helper function to generate colors
export const generatePlantColor = (plantName) => {
    // Use existing color generation logic
    const colorList = [
        "#9CCC65",
        "#FF7043",
        "#4FC3F7",
        "#FBC02D",
        "#BA68C8",
        "#F8BBD0",
        "#26A69A",
        "#EF5350",
        "#7986CB",
        "#81C784",
        "#FFA726",
        "#81D4FA",
        "#DCE775",
        "#CE93D8",
        "#A1887F",
        "#80CBC4",
        "#E57373",
        "#FFB74D",
        "#FFCA28",
        "#81C784",
        "#DCE775",
        "#4DB6AC",
        "#FF8A65",
        "#9575CD",
        "#A1887F",
        "#90CAF9"
    ];

    // Simple hash-based color assignment
    let hash = 0;
    for (let i = 0; i < plantName.length; i++) {
        hash = ((hash << 5) - hash) + plantName.charCodeAt(i);
        hash = hash & hash; // Convert to 32-bit integer
    }

    return colorList[Math.abs(hash) % colorList.length];
};


// fill remaining areas of the planted garden after algorithm runs!
export const fillRemainingOpenAreas = async (gardenMatrix, plantMap, config, plants) => {
    const newMap = plantMap.map(row => [...row]);
    const onlyUserSelected = config.onlyUserSelected || false;
    const useFullSpace = config.useFullSpace || false;
    const strictCounts = config.strictCounts || false;

    // Helper function to separate plants by size
    const separatePlantsBySize = (plantList) => {
        const small = plantList.filter(
            p => (p.planting_width || p.plantingWidth || 1) === 1 &&
                (p.planting_height || p.plantingHeight || 1) === 1
        );

        const medium = plantList.filter(
            p => {
                const width = p.planting_width || p.plantingWidth || 1;
                const height = p.planting_height || p.plantingHeight || 1;
                return (width === 2 || height === 2) && !(width === 1 && height === 1);
            }
        );

        return { small, medium };
    };

    // Helper function to fill spaces with given plants
    const fillWithPlants = (smallPlants, mediumPlants) => {
        // First pass: Try to place larger plants (2x2, 2x1, 1x2)
        for (let row = 0; row < gardenMatrix.length; row++) {
            for (let col = 0; col < gardenMatrix[0].length; col++) {
                const isSoil = gardenMatrix[row][col] === 1;
                const isEmpty = newMap[row][col] === null;

                if (isSoil && isEmpty && mediumPlants.length > 0) {
                    // Try to place a medium-sized plant
                    const shuffledMediumPlants = [...mediumPlants].sort(() => Math.random() - 0.5);

                    for (const plantToUse of shuffledMediumPlants) {
                        // Use your existing validatePlacement function
                        if (validatePlacement(plantToUse, newMap, gardenMatrix, col, row)) {
                            // Place the plant using your existing placePlant function
                            const updatedMap = placePlant(newMap, plantToUse, row, col);

                            // Update our working map
                            for (let r = 0; r < newMap.length; r++) {
                                for (let c = 0; c < newMap[0].length; c++) {
                                    newMap[r][c] = updatedMap[r][c];
                                }
                            }

                            break; // Successfully placed, move to next cell
                        }
                    }
                }
            }
        }

        // Second pass: Fill remaining 1x1 spaces with small plants
        for (let row = 0; row < gardenMatrix.length; row++) {
            for (let col = 0; col < gardenMatrix[0].length; col++) {
                const isSoil = gardenMatrix[row][col] === 1;
                const isEmpty = newMap[row][col] === null;

                if (isSoil && isEmpty && smallPlants.length > 0) {
                    const plantToUse = smallPlants[Math.floor(Math.random() * smallPlants.length)];
                    const mappedPlant = mapPlantProperties(plantToUse);

                    newMap[row][col] = {
                        ...mappedPlant,
                        rootCell: true,
                        color: mappedPlant.color || generatePlantColor(mappedPlant.name)
                    };
                }
            }
        }
    };

    // Phase 1: Use user-selected plants if conditions are met
    if (onlyUserSelected && plants && plants.length > 0 && !strictCounts) {
        const userPlants = separatePlantsBySize(plants);

        if (userPlants.small.length > 0 || userPlants.medium.length > 0) {
            console.log("Filling with user-selected plants first");
            fillWithPlants(userPlants.small, userPlants.medium);
        }
    }

    // Phase 2: Fill remaining spaces with filler plants if useFullSpace is true
    if (useFullSpace) {
        // Check if there are still empty spaces
        let hasEmptySpaces = false;
        outerLoop: for (let row = 0; row < gardenMatrix.length; row++) {
            for (let col = 0; col < gardenMatrix[0].length; col++) {
                if (gardenMatrix[row][col] === 1 && newMap[row][col] === null) {
                    hasEmptySpaces = true;
                    break outerLoop;
                }
            }
        }

        if (hasEmptySpaces) {
            console.log("Filling remaining spaces with filler plants");
            const fillerPlants = await fetchFillerPlants();
            const separatedFillerPlants = separatePlantsBySize(fillerPlants);

            if (separatedFillerPlants.small.length > 0 || separatedFillerPlants.medium.length > 0) {
                fillWithPlants(separatedFillerPlants.small, separatedFillerPlants.medium);
            } else {
                console.warn("No suitable filler plants found for remaining spaces.");
            }
        }
    } else if (!onlyUserSelected || !plants || plants.length === 0 || strictCounts) {
        // Original logic: use filler plants when not using user-selected plants
        const fillerPlants = await fetchFillerPlants();
        const separatedFillerPlants = separatePlantsBySize(fillerPlants);

        if (separatedFillerPlants.small.length > 0 || separatedFillerPlants.medium.length > 0) {
            fillWithPlants(separatedFillerPlants.small, separatedFillerPlants.medium);
        } else {
            console.warn("No suitable plants found for filling remaining areas.");
        }
    }

    return newMap;
};

const fetchFillerPlants = async () => {
    try {
        const res = await fetch('/api/algorithm/fetchFillerPlants');

        const result = await res.json();

        if (!res.ok) {
            console.error('Failed to load Filler Plants:', result.error);
            return {};
        }

        return result.data; // returns all filler plants
    } catch (error) {
        console.error('Fetch error:', error);
    }
};


export const fetchCompanionPlants = async (plants) => {
    try {
        const plantIds = plants.map(p => p.id);

        const res = await fetch('/api/algorithm/fetchCompanionPlants', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ plantIds })
        });

        const result = await res.json();

        if (!res.ok) {
            console.error('Failed to load companions:', result.error);
            return {};
        }

        return result.data; //returns a list of companions sorted by their companion plant
    } catch (error) {
        console.error('Fetch error:', error);
    }
};


export const fetchOrganicPestControlPlants = async () => {
    try {
        const res = await fetch('/api/algorithm/fetchOrganicPestControlPlants');

        const result = await res.json();

        if (!res.ok) {
            console.error('Failed to load Organic Pest Control Plants:', result.error);
            return {};
        }

        return result.data; // returns all organic pest control plants rn
    } catch (error) {
        console.error('Fetch error:', error);
    }
};

export const fetchPollinatorPlants = async () => {
    try {
        const res = await fetch('/api/algorithm/fetchPollinatorPlants');

        const result = await res.json();

        if (!res.ok) {
            console.error('Failed to load Pollinator Plants:', result.error);
            return {};
        }

        return result.data; // returns all pollinator friendly plants rn
    } catch (error) {
        console.error('Fetch error:', error);
    }
};
